import { useState } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { displayName } from "../utils/companyProfile";
import { TRADES_DAYS } from "../utils/insiderTrades";
import InfoTip from "./InfoTip";
import LTR from "./LTR";
import { Amount } from "./Money";

// ─── "בעלי עניין שקנו או מכרו" (sprint 21) ─────────────────────────────────
// Form 4, open-market trades only (P a purchase, S a sale), the last 30
// days, in the user's holdings (utils/secFilings.js fetchInsiderTrades).
// Who, what role, bought or sold, how many, at what price, when · and the
// filing itself. No reading of it: a trade is not a view (the ⓘ says so).

const ddmm = d => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
const SHOWN = 6;

export default function IntelInsiders({ trades }) {
  const [all, setAll] = useState(false);
  if (trades === undefined) return null;   // still loading · the section waits
  const list = trades || [];
  const shown = all ? list : list.slice(0, SHOWN);
  return (
    <section data-intel-insiders style={{ background: UI.surface.card, borderRadius: T.radius.xl, boxShadow: UI.shadow.cardEl, padding: "16px 16px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <h2 style={{ ...TYPE.heading, margin: 0 }}>בעלי עניין שקנו או מכרו</h2>
        <InfoTip label="על עסקאות בעלי עניין" text="עסקה של בעל עניין אינה מעידה על כוונה או על כיוון" size={14} noteStyle={{ flexBasis: "100%" }}/>
      </div>
      <span style={{ fontSize: 13, color: UI.text.muted, marginBottom: 4 }}>
        קנייה ומכירה בשוק הפתוח בהחזקות שלך, <LTR className="si-num">{TRADES_DAYS}</LTR> הימים האחרונים · לפי דיווחי <LTR>Form 4</LTR> ל-<LTR>SEC</LTR>
      </span>

      {trades === null ? (
        <span style={{ fontSize: 14, color: UI.text.secondary, padding: "8px 0 6px" }}>לא ניתן לטעון את הדיווחים כרגע</span>
      ) : !list.length ? (
        <span data-intel-insiders-empty style={{ fontSize: 14, color: UI.text.secondary, padding: "8px 0 6px", lineHeight: 1.5 }}>
          לא דווחו ב-<LTR className="si-num">{TRADES_DAYS}</LTR> הימים האחרונים קניות או מכירות בשוק הפתוח של בעלי עניין בהחזקות שלך
        </span>
      ) : shown.map((t, i) => (
        <div key={`${t.url}|${i}`} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "10px 0", borderTop: `1px solid ${UI.line.hairline}` }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: UI.text.primary, lineHeight: 1.45 }}>
            <LTR style={{ fontFamily: FONT.latin }}>{t.symbol}</LTR> · <LTR style={{ fontFamily: FONT.latin }}>{displayName(t.name)}</LTR>
            {t.title && <span style={{ fontWeight: 400, color: UI.text.secondary }}> · <LTR style={{ fontFamily: FONT.latin }}>{t.title}</LTR></span>}
          </span>
          <span style={{ fontSize: 13.5, color: UI.text.primary }}>
            {t.side === "buy" ? "קנייה" : "מכירה"}
            {t.shares > 0 && <> של <LTR className="si-num">{Math.round(t.shares).toLocaleString("en-US")}</LTR> מניות</>}
            {t.price > 0 && <> במחיר <Amount value={t.price} currency="USD" decimals={2} size={null} weight={600}/> למניה</>}
            {!(t.price > 0) && t.priceLow > 0 && t.priceHigh > 0 && <> במחירים <Amount value={t.priceLow} currency="USD" decimals={2} size={null} weight={600}/>–<Amount value={t.priceHigh} currency="USD" decimals={2} size={null} weight={600}/> למניה</>}
            {t.count > 1 && <>, ב-<LTR className="si-num">{t.count}</LTR> עסקאות</>}
            {" · "}<LTR className="si-num">{ddmm(t.date)}</LTR>
          </span>
          {/^https?:\/\//.test(t.url || "") && (
            <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: UI.interactive, textDecoration: "none", fontWeight: 600, alignSelf: "flex-start" }}>
              הדיווח ב-<LTR>SEC</LTR> (<LTR>Form 4</LTR>)
            </a>
          )}
        </div>
      ))}

      {list.length > SHOWN && (
        <button type="button" onClick={() => setAll(v => !v)}
          style={{ alignSelf: "flex-start", background: "transparent", border: "none", padding: 0, minHeight: 44, fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
          {all ? "פחות" : <>כל <LTR className="si-num">{list.length}</LTR> העסקאות</>}
        </button>
      )}
    </section>
  );
}
